import { Link } from "react-router-dom";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { BgVideo } from "@/components/common/BgVideo";
import { BG_VIDEOS, BG_POSTERS } from "@/config/media";
import { SectionTitle } from "@/components/common/SectionTitle";
import { EventCard } from "@/components/cards/EventCard";
import { PackageCard } from "@/components/cards/PackageCard";
import { PageSections } from "@/components/common/PageSections";
import { usePublicContent } from "@/lib/useContent";
import { useSite } from "@/context/SiteContext";
import * as Icons from "lucide-react";
import {
  Trophy, Users, Zap, ShieldCheck, ExternalLink, Apple, Play, Smartphone,
  UserCheck, ListChecks, CalendarDays, CreditCard, LifeBuoy, ArrowRight,
  Megaphone, Phone, Download,
} from "lucide-react";

const FEATURES = [
  { icon: Trophy, title: "Competitive Club", text: "Train and compete with the Mavs club volleyball program." },
  { icon: Users, title: "All Skill Levels", text: "From first-timers to elite players — everyone develops." },
  { icon: Zap, title: "Athletic Development", text: "Speed, agility, and vertical training built for the court." },
  { icon: ShieldCheck, title: "Safe & Supportive", text: "Certified coaches focused on confidence and safe progression." },
];

// Parent-friendly step-by-step. Each step has big, tappable actions.
const STEPS = [
  { icon: Download, title: "Download the Trilogy App", text: "Get the app on your phone to manage everything in one place." },
  { icon: UserCheck, title: "Create or Login to Your Account", text: "Set up your athlete's profile or sign in to your existing account." },
  { icon: ListChecks, title: "Join the Correct Mavs Group", text: "Select your athlete's age group / program inside the app." },
  { icon: CalendarDays, title: "View the Schedule", text: "Check practice times, tournaments, and events." },
  { icon: CreditCard, title: "Book or Purchase a Package", text: "Reserve a tryout or pick the Mavs membership that fits." },
  { icon: LifeBuoy, title: "Contact Support if Stuck", text: "Our team is here to help parents every step of the way." },
];

const HIGHLIGHTS = [
  { label: "16U Mavs", stat: "Regional Finalists", note: "2024 Club Season" },
  { label: "Athletes Developed", stat: "120+", note: "Across all age groups" },
  { label: "College Commits", stat: "8", note: "Mavs alumni playing collegiately" },
];

export default function MavsVolleyball() { return <PageRenderer pageKey="mavs-volleyball" />; }

export const MavsBody = () => {
  const { data: events } = usePublicContent("events");
  const { data: resources } = usePublicContent("volleyball_resources");
  const { data: packages } = usePublicContent("packages");
  const { data: posts } = usePublicContent("posts");
  const { settings } = useSite();

  const isVb = (s) => /volley|mavs/i.test(s || "");
  const vbEvents = events.filter((e) => isVb(`${e.title} ${e.description}`));
  const schedule = vbEvents.length ? vbEvents : events.slice(0, 3);
  const mavsPackages = packages.filter((p) => ["Mavs Volleyball", "Camps / Clinics"].includes(p.category));
  const announcements = posts.filter((p) => isVb(`${p.title} ${p.excerpt} ${p.source}`));

  const portalUrl = settings.client_portal_url || "https://train.trilogyfitpt.com";
  const bookUrl = settings.booking_intro_url || settings.exercise_booking_url;

  const bigBtn = "inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3.5 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 active:scale-[0.98] sm:w-auto";

  return (
    <div data-testid="mavs-page">
      {/* hero via PageRenderer */}

      {/* Training overview */}
      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="The Program" title="Mavs Training Overview" subtitle="Year-round development that builds skills, strength, and confidence on and off the court." />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} data-testid="mavs-feature" className="rounded-xl border border-border bg-card p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-brand text-white"><Icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 display-title text-2xl">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Parent step-by-step */}
      <section id="mavs-steps" className="scroll-mt-24 border-t border-border bg-card/30 py-16 sm:py-20" data-testid="mavs-steps">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <SectionTitle eyebrow="For Parents" title="Get Started in 6 Steps" subtitle="A simple path to get your athlete training with the Mavs." />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="flex flex-col rounded-xl border border-border bg-card p-6" data-testid={`mavs-step-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-white display-title text-lg">{i + 1}</span>
                    <Icon className="h-5 w-5 text-brand" />
                  </div>
                  <h3 className="mt-4 font-condensed text-lg font-semibold uppercase tracking-wide">{s.title}</h3>
                  <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{s.text}</p>

                  {/* Step actions */}
                  <div className="mt-4 flex flex-col gap-2">
                    {i === 0 && (
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <a href={settings.app_store_url} target="_blank" rel="noopener noreferrer" data-testid="mavs-appstore" className={`${bigBtn} bg-white text-black hover:bg-white/90`}><Apple className="h-4 w-4" /> App Store</a>
                        <a href={settings.google_play_url} target="_blank" rel="noopener noreferrer" data-testid="mavs-googleplay" className={`${bigBtn} bg-white text-black hover:bg-white/90`}><Play className="h-4 w-4" /> Google Play</a>
                      </div>
                    )}
                    {i === 1 && (
                      <Link to="/login" data-testid="mavs-login" className={`${bigBtn} bg-brand text-white hover:bg-[hsl(var(--brand-red-deep))]`}><UserCheck className="h-4 w-4" /> Login / Create Account</Link>
                    )}
                    {i === 2 && (
                      <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="mavs-portal" className={`${bigBtn} border border-border bg-transparent text-foreground hover:bg-accent`}>Open App / Portal <ExternalLink className="h-4 w-4" /></a>
                    )}
                    {i === 3 && (
                      <a href="#mavs-schedule" data-testid="mavs-view-schedule" className={`${bigBtn} border border-border bg-transparent text-foreground hover:bg-accent`}><CalendarDays className="h-4 w-4" /> View Schedule</a>
                    )}
                    {i === 4 && (
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <a href={bookUrl} target="_blank" rel="noopener noreferrer" data-testid="mavs-book" className={`${bigBtn} bg-brand text-white hover:bg-[hsl(var(--brand-red-deep))]`}>Book Tryout</a>
                        <a href="#mavs-packages" data-testid="mavs-view-packages" className={`${bigBtn} border border-border bg-transparent text-foreground hover:bg-accent`}>View Packages</a>
                      </div>
                    )}
                    {i === 5 && (
                      <Link to="/contact" data-testid="mavs-support" className={`${bigBtn} border border-border bg-transparent text-foreground hover:bg-accent`}><LifeBuoy className="h-4 w-4" /> Contact Support</Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* App download band + quick setup */}
      <section id="mavs-app" className="scroll-mt-24 relative overflow-hidden bg-black py-14 text-white grain">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <img src={settings.logo_url} alt={settings.brand_name} className="h-16 w-auto" data-testid="mavs-app-logo" />
            <h2 className="mt-4 display-title text-4xl sm:text-5xl">Get The Trilogy App</h2>
            <p className="mt-3 max-w-md text-white/70">Everything for Mavs parents in one place — schedule, booking, payments, and team updates.</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={settings.app_store_url} target="_blank" rel="noopener noreferrer" data-testid="mavs-band-appstore" className={`${bigBtn} bg-white text-black hover:bg-white/90`}><Apple className="h-5 w-5" /> App Store</a>
              <a href={settings.google_play_url} target="_blank" rel="noopener noreferrer" data-testid="mavs-band-googleplay" className={`${bigBtn} bg-white text-black hover:bg-white/90`}><Play className="h-5 w-5" /> Google Play</a>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-6">
            <h3 className="flex items-center gap-2 font-condensed text-lg font-semibold uppercase tracking-wide"><Smartphone className="h-5 w-5 text-brand" /> Quick App Setup</h3>
            <ol className="mt-4 space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><span className="font-bold text-brand">1.</span> Install the app and open it.</li>
              <li className="flex gap-3"><span className="font-bold text-brand">2.</span> Create an account or log in with your email.</li>
              <li className="flex gap-3"><span className="font-bold text-brand">3.</span> Search and join your athlete's Mavs group.</li>
              <li className="flex gap-3"><span className="font-bold text-brand">4.</span> Enable notifications for schedule updates.</li>
            </ol>
          </div>
        </div>
      </section>

      <PageSections page="mavs-volleyball" />

      {/* Schedule */}
      {schedule.length > 0 && (
        <section id="mavs-schedule" className="scroll-mt-24 bg-background py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Schedule" title="Practices, Tryouts & Tournaments" subtitle="View upcoming Mavs events. Full schedule and check-in live in the Trilogy app." />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {schedule.map((e) => <EventCard key={e.id} item={e} />)}
            </div>
            <div className="mt-8 text-center">
              <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="mavs-full-schedule" className={`${bigBtn} mx-auto border border-border bg-transparent text-foreground hover:bg-accent`}>See Full Schedule in App <ExternalLink className="h-4 w-4" /></a>
            </div>
          </div>
        </section>
      )}

      {/* Packages / Pricing */}
      {mavsPackages.length > 0 && (
        <section id="mavs-packages" className="scroll-mt-24 border-t border-border bg-card/30 py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Pricing" title="Mavs Packages & Camps" subtitle="Memberships and camps for the Mavs program. Checkout is secure on Exercise.com." />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {mavsPackages.map((p) => <PackageCard key={p.id} item={p} />)}
            </div>
            <div className="mt-8 text-center">
              <Link to="/packages" data-testid="mavs-all-packages" className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-brand hover:underline">View All Packages <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className="bg-background py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Latest" title="Mavs Announcements" subtitle="Important updates for parents and athletes." />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {announcements.map((a) => (
                <div key={a.id} data-testid="mavs-announcement" className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
                  {a.image_url && <img src={a.image_url} alt={a.title} loading="lazy" className="h-40 w-full object-cover" />}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="flex items-center gap-1.5 font-condensed text-xs font-semibold uppercase tracking-[0.18em] text-brand"><Megaphone className="h-3.5 w-3.5" /> {a.source || "Mavs Volleyball"}</span>
                    <h3 className="mt-2 display-title text-2xl leading-tight">{a.title}</h3>
                    {a.excerpt && <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Resources */}
      {resources.length > 0 && (
        <section className="border-t border-border bg-card/30 py-16 sm:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
            <SectionTitle eyebrow="Resources" title="Program Resources" subtitle="Handbooks, schedules, and registration for the Mavs program." />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {resources.map((r) => {
                const Icon = Icons[r.icon] || Icons.FileText;
                return (
                  <div key={r.id} data-testid="volleyball-resource-card" className="flex flex-col rounded-xl border border-border bg-card p-6">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand text-white"><Icon className="h-5 w-5" /></div>
                    <h3 className="mt-4 display-title text-2xl">{r.title}</h3>
                    {r.description && <p className="mt-2 flex-1 text-sm text-muted-foreground">{r.description}</p>}
                    {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid="external-exercisecom-link" className="mt-5 inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors">{r.button_label || "Open"} <ExternalLink className="h-4 w-4" /></a>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Athlete highlights */}
      <section id="mavs-highlights" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Mavs Pride" title="Athlete Highlights" subtitle="Celebrating the growth and success of our young athletes." />
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {HIGHLIGHTS.map((h) => (
              <div key={h.label} data-testid="mavs-highlight" className="rounded-xl border border-border bg-card p-7 text-center">
                <p className="display-title text-5xl text-brand">{h.stat}</p>
                <p className="mt-2 font-condensed text-lg font-semibold uppercase tracking-wide">{h.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{h.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parent contact / support CTA — video-backed window (B&W volleyball loop + red overlay) */}
      <div id="mavs-parents" className="scroll-mt-24">
      <BgVideo
        videoUrl={BG_VIDEOS.volleyball}
        poster={BG_POSTERS.volleyball}
        redOpacity={42}
        testid="mavs-parents-window"
        className="border-t border-border"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-20 sm:py-28 text-center">
          <h3 className="display-title text-4xl text-white sm:text-5xl">Questions? We're Here For Parents</h3>
          <p className="mx-auto mt-3 max-w-lg text-white/90">Not sure where to start or which group your athlete fits? Reach out — we'll guide you.</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/contact" data-testid="mavs-contact" className={`${bigBtn} bg-white text-black hover:bg-white/90`}><LifeBuoy className="h-4 w-4" /> Contact / Support</Link>
            <a href={bookUrl} target="_blank" rel="noopener noreferrer" data-testid="mavs-register" className={`${bigBtn} border border-white/40 bg-transparent text-white hover:bg-white/10`}>Register for Tryouts <ExternalLink className="h-4 w-4" /></a>
          </div>
          {settings.phone && (
            <p className="mt-5 flex items-center justify-center gap-2 text-sm text-white/80"><Phone className="h-4 w-4 text-white" /> {settings.phone}</p>
          )}
        </div>
      </BgVideo>
      </div>
    </div>
  );
}
